// assignNumberUtils.ts — reine Helfer für den Nummer-Zuordnen-Dialog (SP4).
// Bewusst OHNE React/DOM/Fetch, damit sie per Vitest (src/**/*.test.ts, node-env)
// getestet werden können. Drei Aufgaben: (1) grober Nummern-Vergleich zum
// Dubletten-Schutz beim Anhängen einer Telefonnummer an einen Kontakt, (2) den
// `communications`-Payload für PUT /contact/:id bauen, ohne Bestandskanäle zu
// verlieren, (3) die Regeln, WAS beim Zuordnen gefragt werden muss.

import type { CommLabel } from "../../utils/contactToFormState";
import { derivePersonEmployment } from "../../utils/derivePersonEmployment";

export type CommType = "phone" | "mobile";

/** Beschriftung der Art fürs Dropdown — eine Quelle für beide Dialogzweige. */
export const COMM_TYPE_LABEL: Record<CommType, string> = {
    phone: "Festnetz",
    mobile: "Mobil",
};

/**
 * Ob die zugeordnete Nummer die private oder die geschäftliche des Kontakts ist
 * (PO 27.07.2026).
 *
 * Die beiden Werte sind ein VERTRAG mit der Kontaktmaske: `ContactDetails`
 * liest die Kanäle über genau diese `label`-Werte (`renderCommRow("Telefon
 * privat", "phone", "privat")`), klein geschrieben und „geschäftlich" mit
 * Umlaut. Weicht hier ein Zeichen ab, landet die Nummer in der Maske im
 * Nirgendwo — genau das passierte bisher, weil dieser Dialog „Telefon"/„Mobil"
 * als Label schrieb.
 */
export type NummerZuordnung = CommLabel;
export const NUMMER_ZUORDNUNGEN: readonly NummerZuordnung[] = ["privat", "geschäftlich"];

/**
 * Dieselbe Angabe STRUKTURIERT (`ContactCommunication.kind`) — ASCII, also
 * „geschaeftlich" ohne Umlaut, konsistent zu `EmailRecipientHit.kind`.
 *
 * Es braucht beides: `kind` ist das Feld, das die Kontaktmaske und die
 * Bereinigung auswerten, `label` der Freitext, aus dem die alte Maske ihre
 * Zeilen liest. Wer nur eines schreibt, verliert die Angabe an der jeweils
 * anderen Stelle.
 */
export type CommKind = "privat" | "geschaeftlich";
export const kindZuLabel = (kind: CommKind): NummerZuordnung =>
    kind === "privat" ? "privat" : "geschäftlich";

/** Eine wählbare Firma („geschäftlich bei …"). */
export interface FirmaOption {
    id: number;
    name: string;
}

/**
 * Die verbundenen Firmen einer Person — Datenbasis der Firmen-Auswahl.
 *
 * Bewusst über `derivePersonEmployment` und mit denselben Vorgaben wie die
 * Kontaktmaske (`ContactDetailsRedesign: employmentFirmen`): Dialog und Akte
 * müssen dieselbe Liste zeigen, sonst wählt man hier eine Firma, die dort gar
 * nicht zur Auswahl stünde. Firmen ohne Id fallen raus — ohne sie ließe sich
 * `companyId` nicht setzen.
 *
 * Der Kontakt muss mit `?include=relations` geladen sein; beide Richtungen
 * zählen, weil die Firma eines Ansprechpartners im Bestand mal als `src`, mal
 * als `dest` steht.
 */
export function firmenDesKontakts(full: unknown): FirmaOption[] {
    const c = (full ?? {}) as {
        relationsSrc?: unknown;
        relations?: unknown;
        relationsDest?: unknown;
    };
    return derivePersonEmployment(
        (c.relationsSrc ?? c.relations) as never,
        c.relationsDest as never,
    )
        .filter((e) => e.companyId != null)
        .map((e) => ({ id: e.companyId as number, name: e.companyName }));
}

/**
 * Was der Zuordnung noch fehlt — `null` heißt: sie kann gespeichert werden.
 *
 * Die Firmen-Frage stellt sich erst ab ZWEI Firmen: bei genau einer ist die
 * Antwort schon bekannt und wird vorbelegt (siehe {@link vorbelegteFirma}), bei
 * keiner gibt es nichts zu wählen — eine geschäftliche Nummer ohne hinterlegte
 * Firma bleibt erlaubt, sonst käme man aus dem Dialog nicht heraus, bevor die
 * Firmenverbindung in der Akte steht. Dieselbe Schwelle zieht der
 * Kontaktdaten-Dialog der Akte (`DataFormDialog`).
 */
export function zuordnungFehler(f: {
    /** Firmen-Kontakt: kennt kein privat/geschäftlich und keine Firmen-Zuordnung. */
    istFirma: boolean;
    kind: CommKind | "";
    firmen: readonly FirmaOption[];
    companyId: number | null;
}): string | null {
    if (f.istFirma) return null;
    if (f.kind !== "privat" && f.kind !== "geschaeftlich") {
        return "Bitte wählen, ob es die private oder die geschäftliche Nummer ist.";
    }
    if (f.kind === "geschaeftlich" && f.firmen.length >= 2 && !f.companyId) {
        return "Bitte die Firma wählen, zu der diese Nummer gehört.";
    }
    return null;
}

/** Genau eine verbundene Firma → keine Frage, die Antwort steht fest. */
export function vorbelegteFirma(firmen: readonly FirmaOption[]): number | null {
    return firmen.length === 1 ? firmen[0].id : null;
}

/**
 * Die `companyId`, die mitgeschrieben wird: nur an einer geschäftlichen Zeile
 * einer Person. Das Backend nullt sie sonst ohnehin
 * (`sanitizeCommunicationKindCompany`) — hier steht dieselbe Regel, damit der
 * Dialog nicht etwas anzeigt, was nachher nicht gespeichert ist.
 */
export function effektiveFirma(
    istFirma: boolean,
    kind: CommKind | "",
    companyId: number | null,
): number | null {
    if (istFirma || kind !== "geschaeftlich") return null;
    return companyId && companyId > 0 ? companyId : null;
}

/** Bestehender Kommunikationseintrag eines Kontakts (Teilmenge von ContactCommunication). */
export interface ExistingComm {
    id?: number;
    type: string;
    label?: string | null;
    value: string;
    isDefault?: boolean;
    /** Strukturierte Art der Bestandszeile — wird unverändert durchgereicht. */
    kind?: CommKind | null;
    /** „Geschäftlich bei Firma X" der Bestandszeile — ebenfalls nur durchgereicht. */
    companyId?: number | null;
    /** Alle Unternehmen der Bestandszeile (seit 02.10.2026) — durchgereicht, sonst hätte der PUT sie auf `companyId` gekürzt. */
    companyIds?: number[] | null;
}

/** Ein `communications`-Element, wie es das Backend beim PUT /contact/:id erwartet. */
export interface CommPayload {
    id?: number;
    type: string;
    label?: string;
    value: string;
    isDefault: boolean;
    kind?: CommKind | null;
    companyId?: number | null;
    companyIds?: number[];
}

/** Die Angaben zur neuen Zeile — Art, privat/geschäftlich und ggf. die Firma. */
export interface NeueNummer {
    type: CommType;
    label: NummerZuordnung;
    /** null an Firmen-Kontakten: dort gibt es die Unterscheidung nicht. */
    kind: CommKind | null;
    companyId: number | null;
}

/** Nur die Ziffern einer Nummer (für Vergleiche); „+", Leerzeichen, Klammern etc. fallen weg. */
export function digitsOnly(raw: string | null | undefined): string {
    return (raw ?? "").replace(/\D/g, "");
}

/**
 * „Kern" einer (deutschen) Nummer: internationales Präfix (00), Ländervorwahl (49)
 * und die nationale Amts-0 werden entfernt, sodass „+49 241 474070", „0241 474070"
 * und „0049241474070" denselben Kern „241474070" ergeben. Rein heuristisch (DE),
 * dient nur dem Dubletten-Schutz — keine echte Rufnummern-Normalisierung.
 */
export function coreNumber(raw: string | null | undefined): string {
    let d = digitsOnly(raw);
    if (d.startsWith("00")) d = d.slice(2); // 0049… → 49…
    if (d.startsWith("49")) d = d.slice(2); // Ländervorwahl weg
    d = d.replace(/^0+/, ""); // nationale Amts-0(en) weg
    return d;
}

/**
 * Grober Gleichheits-Test zweier Telefonnummern über den {@link coreNumber}-Kern:
 * identisch ODER der eine Kern endet auf den anderen (fängt Durchwahl-/Formatier-
 * Unterschiede ab). Zu kurze/leere Eingaben gelten nur bei exakter Gleichheit als
 * gleich.
 */
export function sameNumber(a: string | null | undefined, b: string | null | undefined): boolean {
    const ca = coreNumber(a);
    const cb = coreNumber(b);
    if (!ca || !cb) return false;
    if (ca === cb) return true;
    if (ca.length < 5 || cb.length < 5) return false;
    const [short, long] = ca.length <= cb.length ? [ca, cb] : [cb, ca];
    return long.endsWith(short);
}

/**
 * Heuristik Festnetz/Mobil aus der (deutschen) Nummer: Kern beginnt mit 15/16/17
 * → Mobil, sonst Festnetz. Bewusst konservativ; im Zweifel „phone".
 */
export function guessNumberType(raw: string | null | undefined): CommType {
    return /^1[567]/.test(coreNumber(raw)) ? "mobile" : "phone";
}

/**
 * Firma oder nicht? Gleiche Heuristik wie `CallLogService.isCompanyContact` im
 * Backend, damit Dialog und Anrufliste denselben Kontakt gleich einordnen.
 * `ContactType` ist eine Lookup-Tabelle mit Freitext, kein Enum — deshalb wird
 * auf den Namen geprüft und nicht auf eine Id.
 */
export function istFirmenkontakt(typeLabel: string | null | undefined): boolean {
    const l = (typeLabel ?? "").toLowerCase();
    return l.includes("firma") || l.includes("gesellschaft");
}

/**
 * Das Freitext-`label` der neuen Zeile.
 *
 * Eine Firma hat keine private Rufnummer — dort steht „geschäftlich" fest, ohne
 * dass gefragt wurde. Bei allen anderen Typen (Person, „Sonstige", „Unknown")
 * entscheidet der Anwender: auch die Person, die Ansprechpartnerin einer Firma
 * ist, hat beides — gerade dort ist die Unterscheidung der eigentliche Punkt.
 */
export function labelFuerZeile(istFirma: boolean, kind: CommKind | ""): NummerZuordnung {
    if (istFirma) return "geschäftlich";
    return kindZuLabel(kind === "geschaeftlich" ? "geschaeftlich" : "privat");
}

/**
 * Baut das `communications`-Array für PUT /contact/:id, um EINE Telefonnummer an
 * einen bestehenden Kontakt anzuhängen, ohne vorhandene Kanäle zu verlieren:
 * Bestandseinträge bleiben (mit id + unverändertem isDefault) erhalten, die neue
 * Nummer wird ergänzt. Sie wird nur dann Standard, wenn der Kontakt noch keinen
 * Standard-Telefon-/Mobil-Eintrag hat (verhindert eine
 * UQ_contactComm_default_per_type-Kollision beim Speichern). Ist die Nummer bereits
 * vorhanden (grober {@link sameNumber}-Vergleich im Telefon-/Mobil-Bereich), wird
 * `null` zurückgegeben — es gibt nichts anzuhängen.
 *
 * Die Angaben zur neuen Zeile kommen als {@link NeueNummer}. `label` ist dabei
 * Pflicht: mit einem Default („Telefon"/„Mobil", wie früher hier) landet die
 * Nummer in der Kontaktmaske in keinem der Felder, und niemand merkt es. `kind`
 * und `companyId` gehen zusätzlich mit — sie sind die strukturierte Fassung
 * derselben Angabe.
 *
 * Die BESTANDSZEILEN reichen `kind` und `companyId` mit durch. Der PUT schickt
 * die Kanalliste als Ganzes; ob TypeORM ein fehlendes Feld beim Cascade-Save
 * stehen lässt oder nullt, hängt am Merge-Verhalten und ist nichts, worauf sich
 * eine gepflegte Firmen-Zuordnung verlassen sollte. Mitschicken kostet nichts.
 */
export function buildAppendedCommunications(
    existing: ExistingComm[],
    number: string,
    neu: NeueNummer,
): CommPayload[] | null {
    const num = (number ?? "").trim();
    if (!num) return null;
    const isPhoneLike = (t: string) => t === "phone" || t === "mobile";
    const alreadyThere = existing.some((c) => isPhoneLike(c.type) && sameNumber(c.value, num));
    if (alreadyThere) return null;
    const hasPhoneDefault = existing.some((c) => isPhoneLike(c.type) && c.isDefault);
    const kept: CommPayload[] = existing.map((c) => ({
        ...(c.id != null ? { id: c.id } : {}),
        type: c.type,
        ...(c.label != null ? { label: c.label } : {}),
        value: c.value,
        isDefault: !!c.isDefault,
        ...(c.kind !== undefined ? { kind: c.kind } : {}),
        ...(c.companyId !== undefined ? { companyId: c.companyId } : {}),
        // Mehrere Unternehmen: die volle Liste mitschicken — nur `companyId`
        // hieße für den Server „genau diese eine“ (contactKommunikationFirmen.ts).
        ...(Array.isArray(c.companyIds) ? { companyIds: c.companyIds } : {}),
    }));
    kept.push({
        type: neu.type,
        value: num,
        label: neu.label,
        isDefault: !hasPhoneDefault,
        kind: neu.kind,
        companyId: neu.companyId,
    });
    return kept;
}

/**
 * Vorbelegung der vollständigen Kontaktanlage (`openContactFormWindow`) mit der
 * Nummer des Anrufs. Die Feldnamen sind ein Vertrag mit den Anlage-Formularen:
 *  - Privatkontakt (`privat_contact_form`): `phonePrivate` / `phoneWork` /
 *    `mobilePrivate` / `mobileWork` — je nach Festnetz/Mobil und privat/
 *    geschäftlich (`buildPrivateContactCreatePayload` macht daraus Zeilen mit
 *    passendem `label` und `kind`).
 *  - Firmenkontakt (`companyForm`): `phone` / `mobile` — eine Firma kennt kein
 *    „privat".
 * Ohne Nummer bleibt das Formular leer.
 */
export function kontaktanlageVorbelegung(p: {
    istFirma: boolean;
    type: CommType;
    kind: CommKind;
    number: string;
}): { kind: "private" | "company"; initialValues: Record<string, string> } {
    const num = (p.number ?? "").trim();
    if (p.istFirma) {
        return { kind: "company", initialValues: num ? { [p.type]: num } : {} };
    }
    const feld = `${p.type}${p.kind === "geschaeftlich" ? "Work" : "Private"}`;
    return { kind: "private", initialValues: num ? { [feld]: num } : {} };
}
